import type { ReactNode } from 'react'
import { cn } from '../lib/utils'
import { ParsleyMark } from './parsley-mark'

// The amber "Pro Level-ups" badge: the Parsley mark + a short label, in the es-amber
// palette. A single branded artifact that had drifted into three hand-rolled copies
// across the meal-plan surfaces. Two shapes:
//   • read-only (default)       — a static pill, e.g. on a Past Plans row.
//   • interactive (onInfoClick) — the whole pill is a button that opens an explainer,
//                                 with a trailing "i" affordance and a subtle hover lift.
// This is the single source of truth for the amber level-up badge — `Chip` deliberately
// has no level-up variant, so there is only one way to render it.

// No `leading-*` override: `text-xs` carries the type scale's paired 12/16 line-height,
// which is what `Chip`'s read-only `meta` shape uses too. A `leading-none` here made the
// pill ~4px shorter than every chip it sits beside — the plan-row status badge, and the
// meta chips on the finalize-draft cards (#1088).
const base =
  'inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-hairline bg-amber-wash py-1 pl-2 pr-3 font-sans text-xs font-semibold tracking-[0.01em] text-amber-strong'

interface LevelUpsChipProps {
  /** The label, already translated — e.g. "Pro Level-ups". */
  children: ReactNode
  /** When set, the whole pill becomes a button that opens an explainer; renders a trailing "i". */
  onInfoClick?: () => void
  /** Tooltip / accessible title for the interactive pill, e.g. "What's Pro Level-ups?". */
  infoLabel?: string
  className?: string
}

/** The small circled "i" that marks the interactive pill as tappable for an explainer. */
function InfoMark() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-[13px] w-[13px] items-center justify-center rounded-full border border-current text-[8.5px] font-bold italic opacity-60">
      i
    </span>
  )
}

export function LevelUpsChip({ children, onInfoClick, infoLabel, className }: LevelUpsChipProps) {
  if (onInfoClick) {
    return (
      <button
        type="button"
        onClick={onInfoClick}
        title={infoLabel}
        className={cn(
          base,
          'transition-[background,border-color,transform] duration-[160ms] hover:-translate-y-px active:translate-y-0 motion-reduce:transition-none motion-reduce:hover:translate-y-0',
          'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:focus-visible:ring-pelorous-300',
          className
        )}>
        <ParsleyMark filled className="h-3 w-3 text-amber" />
        {children}
        <InfoMark />
      </button>
    )
  }

  return (
    <span className={cn(base, className)}>
      <ParsleyMark filled className="h-3 w-3 text-amber" />
      {children}
    </span>
  )
}
